"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";
import { MOT_COOKIE_PREFERENCES_CHANGED } from "@/components/CookieConsent";
import { isMarketingAllowed } from "@/lib/analytics-consent";
import { jsonForInlineScript } from "@/lib/ga4-consent";
import { isProdPublicTrackingEnabled } from "@/lib/public-deploy-env";
import { onMarketingAllowanceChanged } from "@/lib/vendor-tracking-lifecycle";

declare global {
  interface Window {
    rdt?: (...args: unknown[]) => void;
  }
}

const REDDIT_PIXEL_ID = /^[A-Za-z0-9_-]{5,64}$/;

export function RedditPixelLoader({ pixelId }: { pixelId: string | undefined }) {
  const pathname = usePathname();
  const [enabled, setEnabled] = useState(false);
  const [generation, setGeneration] = useState(0);
  const previousPath = useRef<string | null>(null);
  const wasEnabled = useRef(false);
  const prod = isProdPublicTrackingEnabled();
  const id = pixelId?.trim() ?? "";
  const validId = REDDIT_PIXEL_ID.test(id);

  const applyAllowance = useCallback((allowed: boolean) => {
    if (!allowed) {
      onMarketingAllowanceChanged(false);
      wasEnabled.current = false;
      previousPath.current = null;
      setEnabled(false);
      return;
    }
    if (!wasEnabled.current) setGeneration((value) => value + 1);
    wasEnabled.current = true;
    setEnabled(true);
  }, []);

  useEffect(() => {
    if (!prod || !validId) return;
    applyAllowance(isMarketingAllowed());
    const onPreferencesChanged = () => applyAllowance(isMarketingAllowed());
    window.addEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPreferencesChanged);
    return () => window.removeEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPreferencesChanged);
  }, [applyAllowance, prod, validId]);

  useEffect(() => {
    if (!enabled) return;
    if (previousPath.current === null) {
      previousPath.current = pathname;
      return;
    }
    if (previousPath.current !== pathname && typeof window.rdt === "function") {
      window.rdt("track", "PageVisit");
      previousPath.current = pathname;
    }
  }, [enabled, pathname]);

  if (!prod || !validId || !enabled) return null;
  const safeId = jsonForInlineScript(id);
  return (
    <Script id={`mot-reddit-pixel-${generation}`} strategy="afterInteractive">
      {`!function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var s=d.createElement('script');s.async=!0;s.src='https://www.redditstatic.com/ads/pixel.js';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x)}}(window,document);rdt('init',${safeId},{optOut:false});rdt('track','PageVisit');`}
    </Script>
  );
}
